import React, { useEffect, useState } from 'react'
import { ChakraProvider } from '@chakra-ui/react'
import { Box, chemistryTheme, Container, Flex, Heading } from 'chemistry'
import ProductBreadCrumbs from './components/BreadCrumbs/ProductBreadCrumbs'
import DetailImage from './components/DetailImage/DetailImage'
import ProductUsp from './components/ProductUsp'
import BannerDiscountSwitcher from './components/Banners/discount/BannerDiscountSwitcher'
import { ProductVariants } from './components/ProductVariants/ProductVariants'
import ProductPrice from './components/ProductPrice'
import ConfiguratorBanner from './components/Banners/ConfiguratorBanner'
import UspBanner from './components/Banners/UspBanner/UspBanner'
import SidePanel from './components/SidePanel/SidePanel'
import ProductDetailsDescription from './components/Description'
import { useProductViewedTrack } from './hooks/useProductViewedTrack'
import { useInjectFragment } from './hooks/useInjectFragment'
import { useProductAnalytic } from './hooks/useProductAnalytic'
import { getUsp } from './selectors/usp'
import { getProductAdditionalEans, getProductGtin, getProductName } from '../../common/selectors/product'
import ProductSuggestions from './components/ProductSuggestions'
import BazaarvoiceContainer from './components/Bazarvoice/BazaarvoiceContainer'
import BazaarvoiceRatingSummary from './components/Bazarvoice/BazaarvoiceRatingSummary'
import Ean from 'client/fragments/productDetailsFragmentInfo/components/Ean'
import { TranslateContextProvider } from 'client/common/translator'
import { isLdFlagInABTest } from './utils/abTestsUtils'
import { CHANGE_PDP_BUNDLE_POSITION, SHOW_EMPTY_REVIEWS } from '../../../common/constants'
import { checkIfTemporarilyHideVLP } from './checkIfTemporarilyHideVLP'
import { ShareButton } from './components/ShareButton'
import { getDiscountData } from './components/DiscountPercentage'

const showPrice = productDetails =>
  !productDetails.disableStatus?.isDisabled &&
  !productDetails.discontinued &&
  (!!productDetails.price?.value || !!productDetails.offers?.[0]?.price?.value)

const Component = ({
  productDetails,
  withBreadcrumbs,
  lang,
  format,
  host,
  isIpFromShop,
  isGuest,
  productId,
  showPraxisPlusHint,
  productUrl,
  pimAttributesTranslations,
  translationsPdp,
  contentfulTranslations,
  showProductCardAvailabilityStatus,
  allowWishlist,
  productsTogglesToDataLayer,
  changePdpBundlePosition,
  showEmptyReviews,
  focusStylesStr,
  inApp
}) => {
  useProductViewedTrack(productId)
  useProductAnalytic(productDetails)
  useInjectFragment({
    eventName: 'BASKET_FRAGMENT_RENDERED',
    elementId: 'pdpAddToBasket',
    containerId: 'basket-container'
  })
  useInjectFragment({
    eventName: 'BUNDLE_FRAGMENT_RENDERED',
    elementId: 'bundleFragment',
    containerId: 'bundle-container'
  })
  useEffect(() => {
    insertBVScript(lang, format)(document)
  }, [lang, format])

  useEffect(() => {
    if (isLdFlagInABTest(CHANGE_PDP_BUNDLE_POSITION, productsTogglesToDataLayer)) {
      window?.dataLayer?.push({
        event: 'ldFlaggedFeature',
        ldFlag: CHANGE_PDP_BUNDLE_POSITION,
        ldFlagStatus: !!changePdpBundlePosition,
        ldVariation: changePdpBundlePosition
      })
    }
    if (isLdFlagInABTest(SHOW_EMPTY_REVIEWS, productsTogglesToDataLayer)) {
      window?.dataLayer?.push({
        event: 'ldFlaggedFeature',
        ldFlag: SHOW_EMPTY_REVIEWS,
        ldFlagStatus: true,
        ldVariation: showEmptyReviews
      })
    }
  }, [changePdpBundlePosition, showEmptyReviews, productsTogglesToDataLayer])

  const [sidePanelToggled, setSidePanelToggled] = useState(false)

  const ean = getProductGtin(productDetails)
  const additionalEans = getProductAdditionalEans(productDetails)
  const name = getProductName(productDetails)
  const productSuggestionsProps = { productDetails, breadcrumbs: productDetails?.breadcrumbs, lang }
  const reviewsProps = {
    productId,
    showEmptyReviews,
    productsTogglesToDataLayer
  }
  const hideVLP = checkIfTemporarilyHideVLP({ productId, format })

  const discountData = getDiscountData(productDetails)

  return (
    <ChakraProvider resetCSS={false} theme={chemistryTheme}>
      <style dangerouslySetInnerHTML={{ __html: focusStylesStr }}></style>
      <TranslateContextProvider translations={translationsPdp}>
        <Container paddingInline={'1rem'} spacing={{}}>
          <div className='mxd-product-detail-layout'>
            {withBreadcrumbs && <ProductBreadCrumbs breadcrumbs={productDetails?.breadcrumbs} />}
            {!withBreadcrumbs && <Box height='24px' />}
            <DetailImage
              {...{
                productDetails,
                lang,
                format,
                showProductCardAvailabilityStatus,
                allowWishlist,
                productId,
                isGuest,
                currentLocation: productUrl,
                hideVLP
              }}
            />
            <Heading as='h1' className='mxd-product-detail-layout-heading' itemProp='name' my={0}>
              {name}
            </Heading>
            {isIpFromShop && <Ean ean={ean} additionalEans={additionalEans} />}
            <div className='mxd-product-detail-layout-info'>
              {inApp ? (
                <Flex width='100%' alignItems='center' justifyContent='space-between'>
                  <BazaarvoiceRatingSummary {...reviewsProps} />
                  <ShareButton name={name} format={format} />
                </Flex>
              ) : (
                <BazaarvoiceRatingSummary {...reviewsProps} />
              )}
              <ProductUsp usps={getUsp({ productDetails })} lang={lang} />
              <BannerDiscountSwitcher {...{ productDetails, discountData, lang, format }} />
              <ProductVariants product={productDetails} />
              <Box my='md'>
                <ProductPrice
                  {...{
                    product: productDetails,
                    productId,
                    format,
                    lang,
                    host,
                    isGuest,
                    currentLocation: productUrl,
                    showPraxisPlusHint,
                    showPrice: showPrice(productDetails),
                    name,
                    hideVLP,
                    setSidePanelToggled,
                    discountData
                  }}
                />
              </Box>
              <ConfiguratorBanner productDetails={productDetails} />
              <Box id='basket-container' />
            </div>
            <ProductSuggestions {...productSuggestionsProps} />
          </div>
          <SidePanel sidePanelToggled={sidePanelToggled} setSidePanelToggled={setSidePanelToggled} format={format} />
          <UspBanner contentfulTranslations={contentfulTranslations} />
          {!changePdpBundlePosition && <div id='bundle-container' />}
          <ProductDetailsDescription
            {...{
              lang,
              productDetails,
              pimAttributesTranslations
            }}
          />
          {changePdpBundlePosition && <div id='bundle-container' />}
          <BazaarvoiceContainer {...reviewsProps} />
        </Container>
      </TranslateContextProvider>
    </ChakraProvider>
  )
}

function insertBVScript(lang, format) {
  const productionHosts = ['www.praxis.nl', 'www.brico.be']
  const environment = productionHosts.includes(window?.location?.host) ? 'production' : 'staging'
  const domain = format === 'praxis' ? 'praxis-nl' : 'brico-be'
  const locales = {
    'praxis-nl': 'nl_NL',
    'brico-be': lang === 'fr' ? 'fr_BE' : 'nl_BE'
  }

  const src = `https://apps.bazaarvoice.com/deployments/${domain}/main_site/${environment}/${locales[domain]}/bv.js`

  return function (d, script) {
    script = d.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.onload = function () {
      // remote script has loaded
    }
    script.src = src
    d.getElementsByTagName('head')[0].appendChild(script)
  }
}

export default { Component }
